import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { PlainButton } from "../Ui/index.jsx";

// A small info icon that reveals an explanation on click — for the "how does
// this actually work" question that keeps coming up on both onboarding pages.
// Click-to-toggle rather than hover, so it works the same on touch and desktop
// and doesn't disappear while you're reading it. The look is the shared
// `ds-infotip` in design-system.css; only the width comes from the caller.
export default function InfoTooltip({ children, width = 340, title = "How this page works" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panel = useRef(null);
  // The panel opens under the button, right-aligned. When the button sits near the left edge of a phone that would
  // push it off screen, so it is slid back inside the window by exactly the overhang.
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!open || !panel.current) { setShift(0); return; }
    const r = panel.current.getBoundingClientRect();
    const margin = 8;
    const over = r.left - shift < margin ? margin - (r.left - shift) : Math.min(0, window.innerWidth - margin - (r.right - shift));
    setShift(over);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="ds-infotip">
      <PlainButton
        type="button"
        className="ds-infotip__btn"
        onClick={() => setOpen((v) => !v)}
        title={title}
        aria-label={title}
        aria-expanded={open}
      >
        <Info size={16} />
      </PlainButton>
      {open && <div ref={panel} className="ds-infotip__panel" style={{ "--infotip-w": `${width}px`, "--infotip-shift": `${shift}px` }}>{children}</div>}
    </div>
  );
}
